/**
 * Frontend translations for peptide research metadata.
 * Values come from Saleor product metadata (may be English or German),
 * translated here to avoid modifying the API data model.
 */

import { getSalesCountryCopy, type SalesCountryCopyKey } from "@/config/sales-country-copy";
import nbMetadata from "@/config/nb-metadata-copy.json";

/** limited/moderate/established/extensive mirror `peptides.level.*` in every catalog (see peptide-translations.test.ts). */
export const RESEARCH_LEVEL_LABELS: Record<string, Record<string, string>> = {
	limited: {
		de: "Begrenzt",
		en: "Limited",
		es: "Limitado",
		tr: "Sınırlı",
		fr: "Limité",
		it: "Limitato",
		nl: "Beperkt",
		pl: "Ograniczony",
		pt: "Limitado",
		sv: "Begränsad",
		da: "Begrænset",
		cs: "Omezený",
		nb: nbMetadata["researchLevel.limited"],
		et: "Piiratud",
		lv: "Ierobežots",
		fi: "Rajallinen",
		hr: "Ograničeno",
		hu: "Korlátozott",
		ro: "Limitat",
		sl: "Omejeno",
		el: "Περιορισμένο",
		mt: "Limitat",
		sk: "Obmedzený",
		ga: "Teoranta",
		bg: "Ограничен",
	},
	moderate: {
		de: "Moderat",
		en: "Moderate",
		es: "Moderado",
		tr: "Orta",
		fr: "Modéré",
		it: "Moderato",
		nl: "Matig",
		pl: "Umiarkowany",
		pt: "Moderado",
		sv: "Måttlig",
		da: "Middel",
		cs: "Mírný",
		nb: nbMetadata["researchLevel.moderate"],
		et: "Mõõdukas",
		lv: "Mērens",
		fi: "Kohtalainen",
		hr: "Umjereno",
		hu: "Mérsékelt",
		ro: "Moderat",
		sl: "Zmerno",
		el: "Μέτριο",
		mt: "Moderat",
		sk: "Stredný",
		ga: "Measartha",
		bg: "Умерен",
	},
	established: {
		de: "Etabliert",
		en: "Established",
		es: "Establecido",
		tr: "Yerleşik",
		fr: "Établi",
		it: "Consolidato",
		nl: "Gevestigd",
		pl: "Ugruntowany",
		pt: "Estabelecido",
		sv: "Etablerad",
		da: "Etableret",
		cs: "Etablovaný",
		nb: nbMetadata["researchLevel.established"],
		et: "Väljakujunenud",
		lv: "Nostiprināts",
		fi: "Vakiintunut",
		hr: "Etablirano",
		hu: "Megalapozott",
		ro: "Consacrat",
		sl: "Uveljavljeno",
		el: "Καθιερωμένο",
		mt: "Stabbilit",
		sk: "Etablovaný",
		ga: "Bunaithe",
		bg: "Утвърден",
	},
	extensive: {
		de: "Umfangreich",
		en: "Extensive",
		es: "Extenso",
		tr: "Kapsamlı",
		fr: "Étendu",
		it: "Esteso",
		nl: "Uitgebreid",
		pl: "Rozległy",
		pt: "Extenso",
		sv: "Omfattande",
		da: "Omfattende",
		cs: "Rozsáhlý",
		nb: nbMetadata["researchLevel.extensive"],
		et: "Ulatuslik",
		lv: "Plašs",
		fi: "Laaja",
		hr: "Opsežno",
		hu: "Kiterjedt",
		ro: "Extins",
		sl: "Obsežno",
		el: "Εκτενές",
		mt: "Estensiv",
		sk: "Rozsiahly",
		ga: "Forleathan",
		bg: "Обширен",
	},
	emerging: {
		de: "Aufkommend",
		en: "Emerging",
		it: "Emergente",
		es: "Emergente",
		fr: "Émergent",
		nl: "Opkomend",
		pt: "Emergente",
		pl: "Wschodzący",
		nb: nbMetadata["researchLevel.emerging"],
		et: "Esilekerkiv",
		lv: "Topošs",
		fi: "Kehittyvä",
		hr: "U nastajanju",
		hu: "Feltörekvő",
		ro: "Emergent",
		sl: "V nastajanju",
		el: "Αναδυόμενο",
		mt: "Emerġenti",
		sk: "Nastupujúci",
		ga: "Ag teacht chun cinn",
		bg: "Нововъзникващ",
	},
	experimental: {
		de: "Experimentell",
		en: "Experimental",
		it: "Sperimentale",
		es: "Experimental",
		fr: "Expérimental",
		nl: "Experimenteel",
		pt: "Experimental",
		pl: "Eksperymentalny",
		nb: nbMetadata["researchLevel.experimental"],
		et: "Eksperimentaalne",
		lv: "Eksperimentāls",
		fi: "Kokeellinen",
		hr: "Eksperimentalno",
		hu: "Kísérleti",
		ro: "Experimental",
		sl: "Eksperimentalno",
		el: "Πειραματικό",
		mt: "Sperimentali",
		sk: "Experimentálny",
		ga: "Turgnamhach",
		bg: "Експериментален",
	},
};

export const AVAILABILITY_LABELS: Record<string, Record<string, string>> = {
	"Research Only": {
		de: "Nur für Forschung",
		en: "Research Only",
		it: "Solo per ricerca",
		es: "Solo para investigación",
		fr: "Recherche uniquement",
		nl: "Alleen voor onderzoek",
		pt: "Apenas para investigação",
		pl: "Tylko do badań",
		nb: nbMetadata["availability.Research Only"],
		et: "Ainult uurimistööks",
		lv: "Tikai pētniecībai",
		fi: "Vain tutkimuskäyttöön",
		hr: "Samo za istraživanje",
		hu: "Csak kutatási célra",
		ro: "Doar pentru cercetare",
		sl: "Samo za raziskave",
		el: "Μόνο για έρευνα",
		mt: "Għar-riċerka biss",
		sk: "Iba na výskum",
		ga: "Chun taighde amháin",
		bg: "Само за изследователски цели",
	},
};

const TIME_UNIT_LABELS: Record<string, Record<string, string>> = {
	hours: {
		de: "Stunden",
		en: "hours",
		it: "ore",
		es: "horas",
		fr: "heures",
		nl: "uur",
		pt: "horas",
		pl: "godzin",
		et: "tundi",
		lv: "Stundas",
		fi: "tuntia",
		hr: "sati",
		hu: "óra",
		ro: "ore",
		sl: "ure",
		el: "Ώρες",
		mt: "Sigħat",
		sk: "Hodiny",
		ga: "Uaireanta",
		bg: "Часове",
	},
	days: {
		de: "Tage",
		en: "days",
		it: "giorni",
		es: "días",
		fr: "jours",
		nl: "dagen",
		pt: "dias",
		pl: "dni",
		et: "päeva",
		lv: "Dienas",
		fi: "päivää",
		hr: "dani",
		hu: "nap",
		ro: "zile",
		sl: "dnevi",
		el: "Ημέρες",
		mt: "Ġranet",
		sk: "Dni",
		ga: "Laethanta",
		bg: "Дни",
	},
	weeks: {
		de: "Wochen",
		en: "weeks",
		it: "settimane",
		es: "semanas",
		fr: "semaines",
		nl: "weken",
		pt: "semanas",
		pl: "tygodni",
		et: "nädalat",
		lv: "Nedēļas",
		fi: "viikkoa",
		hr: "tjedni",
		hu: "hét",
		ro: "săptămâni",
		sl: "tedni",
		el: "Εβδομάδες",
		mt: "Ġimgħat",
		sk: "Týždne",
		ga: "Seachtainí",
		bg: "Седмици",
	},
	week: {
		de: "Woche",
		en: "week",
		it: "settimana",
		es: "semana",
		fr: "semaine",
		nl: "week",
		pt: "semana",
		pl: "tydzień",
		et: "nädal",
		lv: "Nedēļa",
		fi: "viikko",
		hr: "tjedan",
		hu: "hét",
		ro: "săptămână",
		sl: "teden",
		el: "Εβδομάδα",
		mt: "Ġimgħa",
		sk: "Týždeň",
		ga: "Seachtain",
		bg: "Седмица",
	},
	minutes: {
		de: "Minuten",
		en: "minutes",
		it: "minuti",
		es: "minutos",
		fr: "minutes",
		nl: "minuten",
		pt: "minutos",
		pl: "minut",
		et: "minutit",
		lv: "Minūtes",
		fi: "minuuttia",
		hr: "minute",
		hu: "perc",
		ro: "minute",
		sl: "minute",
		el: "Λεπτά",
		mt: "Minuti",
		sk: "Minúty",
		ga: "Nóiméid",
		bg: "Минути",
	},
};

/**
 * German → canonical English mappings for time units that appear in Saleor metadata.
 * Applied before TIME_UNIT_LABELS translation so German source values are handled.
 */
const DE_TO_EN_TIME_UNITS: Record<string, string> = {
	Stunden: "hours",
	Stunde: "hours",
	Tage: "days",
	Tag: "days",
	Wochen: "weeks",
	Woche: "week",
	Minuten: "minutes",
	Minute: "minutes",
};

/**
 * Common German phrases that appear in peptide metadata dosage/context fields.
 * Translated at the phrase level (after time unit replacement).
 */
const PHRASE_TRANSLATIONS: Record<string, Record<string, string>> = {
	Forschungskontext: {
		en: "research context",
		it: "contesto di ricerca",
		es: "contexto de investigación",
		fr: "contexte de recherche",
		nl: "onderzoekscontext",
		pt: "contexto de investigação",
		pl: "kontekst badawczy",
		de: "Forschungskontext",
		et: "uurimiskontekst",
		lv: "Pētniecības konteksts",
		fi: "tutkimuskonteksti",
		hr: "kontekst istraživanja",
		hu: "kutatási kontextus",
		ro: "Context de cercetare",
		sl: "Raziskovalni kontekst",
		el: "Ερευνητικό πλαίσιο",
		mt: "Kuntest tar-riċerka",
		sk: "Výskumný kontext",
		ga: "Comhthéacs taighde",
		bg: "Изследователски контекст",
	},
	"ein- bis zweimal täglich": {
		en: "once or twice daily",
		it: "una o due volte al giorno",
		es: "una o dos veces al día",
		fr: "une à deux fois par jour",
		nl: "één tot twee keer per dag",
		pt: "uma a duas vezes por dia",
		pl: "raz lub dwa razy dziennie",
		de: "ein- bis zweimal täglich",
		et: "üks kuni kaks korda päevas",
		lv: "vienu līdz divas reizes dienā",
		fi: "kerran tai kahdesti päivässä",
		hr: "jednom do dvaput dnevno",
		hu: "naponta egyszer-kétszer",
		ro: "o dată sau de două ori pe zi",
		sl: "enkrat do dvakrat dnevno",
		el: "μία έως δύο φορές την ημέρα",
		mt: "darba sa darbtejn kuljum",
		sk: "raz až dvakrát denne",
		ga: "uair nó dhó in aghaidh an lae",
		bg: "един до два пъти дневно",
	},
	subkutan: {
		en: "subcutaneous",
		it: "sottocutaneo",
		es: "subcutáneo",
		fr: "sous-cutané",
		nl: "subcutaan",
		pt: "subcutâneo",
		pl: "podskórnie",
		de: "subkutan",
		et: "subkutaanne",
		lv: "subkutāni",
		fi: "subkutaanisesti",
		hr: "supkutano",
		hu: "szubkután",
		ro: "subcutanat",
		sl: "subkutano",
		el: "υποδόρια",
		mt: "taħt il-ġilda",
		sk: "subkutánne",
		ga: "fo-chraicneach",
		bg: "подкожно",
	},
};

/**
 * Per-product translations for free-text metadata fields.
 * Keyed by product slug, then locale.
 * English entries are unnecessary - English is the raw metadata fallback.
 */
export const PEPTIDE_TRANSLATIONS: Record<string, Record<string, { mechanism?: string; dosage?: string }>> = {
	"bpc-157": {
		de: {
			mechanism:
				"Fördert die Expression von Wachstumshormonrezeptoren; moduliert VEGF und Angiogenese; unterstützt Kollagensynthese.",
			dosage: "250-500 mcg ein- bis zweimal täglich, subkutan",
		},
	},
};

/** Translate a research level value. Falls back to the raw value. */
export function translateResearchLevel(value: string, locale: string): string {
	const key = value.toLowerCase();
	const labels = RESEARCH_LEVEL_LABELS[key];
	if (!labels) return value;
	return (
		labels[locale] ?? getSalesCountryCopy(locale, `researchLevel.${key}` as SalesCountryCopyKey) ?? value
	);
}

/** Translate an availability value. Falls back to the raw value. */
export function translateAvailability(value: string, locale: string): string {
	const labels = AVAILABILITY_LABELS[value];
	if (!labels) return value;
	return (
		labels[locale] ?? getSalesCountryCopy(locale, `availability.${value}` as SalesCountryCopyKey) ?? value
	);
}

/**
 * Replace time units in a string. Handles both English ("~4-6 hours") and
 * German ("~6 Tage") source values — normalizes German to English first,
 * then translates to target locale. Also translates common German phrases.
 */
export function translateTimeUnits(value: string, locale: string): string {
	let result = value;

	// Step 1: Normalize German time units to English canonical form
	for (const [de, en] of Object.entries(DE_TO_EN_TIME_UNITS)) {
		result = result.replace(new RegExp(`\\b${de}\\b`, "g"), en);
	}

	// Step 2: Translate English canonical form to target locale
	for (const [en, translations] of Object.entries(TIME_UNIT_LABELS)) {
		const localized =
			translations[locale] ?? getSalesCountryCopy(locale, `timeUnit.${en}` as SalesCountryCopyKey) ?? en;
		result = result.replace(new RegExp(`\\b${en}\\b`, "gi"), localized);
	}

	// Step 3: Translate common German phrases
	for (const [phrase, translations] of Object.entries(PHRASE_TRANSLATIONS)) {
		const localized =
			translations[locale] ??
			getSalesCountryCopy(locale, `metadataPhrase.${phrase}` as SalesCountryCopyKey) ??
			phrase;
		if (result.includes(phrase)) {
			result = result.replace(phrase, localized);
		}
	}

	return result;
}

/**
 * Translate per-product free-text fields.
 * Priority: per-product translation → phrase/unit translation → raw value.
 */
export function translatePeptideField(
	slug: string,
	locale: string,
	field: "mechanism" | "dosage",
	fallback: string,
): string {
	const perProduct = PEPTIDE_TRANSLATIONS[slug]?.[locale]?.[field];
	if (perProduct) return perProduct;
	// For dosage, apply time unit + phrase translations to the raw value
	if (field === "dosage") return translateTimeUnits(fallback, locale);
	return fallback;
}
